import { useEffect, lazy, Suspense } from "react";
import StructuredData from "@/components/StructuredData";

// Critical "above the fold" components - Eager load for SEO
import HeroSection from "@/components/landing/HeroSection";
import GooglePartnerSection from "@/components/landing/GooglePartnerSection";

// Below the fold sections - Lazy load for performance
const TestimonialsSection = lazy(() => import("@/components/landing/TestimonialsSection"));
const FraudClickProtectionSection = lazy(() => import("@/components/landing/FraudClickProtectionSection"));
const ServicesPackagesSection = lazy(() => import("@/components/landing/ServicesPackagesSection"));
const FAQSection = lazy(() => import("@/components/landing/FAQSection"));
const FinalCTASection = lazy(() => import("@/components/landing/FinalCTASection"));
const RecentBlogPostsSection = lazy(() => import("@/components/landing/RecentBlogPostsSection"));
const SEOKeywordTags = lazy(() => import("@/components/landing/SEOKeywordTags"));

// Loading fallback component
const SectionSkeleton = () => (
  <div className="w-full py-16 flex items-center justify-center">
    <div className="animate-pulse flex space-x-4">
      <div className="h-12 w-12 bg-muted rounded-full"></div>
      <div className="flex-1 space-y-4 py-1">
        <div className="h-4 bg-muted rounded w-3/4"></div>
        <div className="space-y-2">
          <div className="h-4 bg-muted rounded"></div>
          <div className="h-4 bg-muted rounded w-5/6"></div>
        </div>
      </div>
    </div>
  </div>
);

const Home = () => {
  useEffect(() => {
    // SEO Meta Etiketleri - Kanonik anahtar kelimeler
    const SEO_TITLE = "Google Reklam Ajansı | Dijital Mustafa - Google Ads Uzmanı & Danışmanlığı";
    const SEO_DESC = "Google Reklam Ajansı Dijital Mustafa — Google Ads Uzmanı olarak 18 yıllık Google Ads danışmanlığı deneyimimle Google Ads reklam uzmanı hizmeti sunuyorum. Google Partner sertifikalı profesyonel kampanya yönetimi.";
    document.title = SEO_TITLE;
    
    // Description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', SEO_DESC);

    // OG Tags
    const ogTags = [
      { property: 'og:title', content: SEO_TITLE },
      { property: 'og:description', content: SEO_DESC },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'Reklam Uzmanı' },
      { property: 'og:locale', content: 'tr_TR' },
      { property: 'og:url', content: 'https://www.reklam-uzmani.com.tr' },
      { property: 'og:image', content: 'https://www.reklam-uzmani.com.tr/images/reklam-uzmani-logo.png' },
    ];

    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    // Twitter Cards
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: SEO_TITLE },
      { name: 'twitter:description', content: SEO_DESC },
      { name: 'twitter:image', content: 'https://www.reklam-uzmani.com.tr/images/reklam-uzmani-logo.png' },
    ];

    twitterTags.forEach(({ name, content }) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });
  }, []);

  return (
    <div className="bg-white">
      <StructuredData />
      
      {/* Sadeleştirilmiş 7 Bölümlü Yapı */}
      {/* Critical sections - No lazy loading for better SEO */}
      <HeroSection />
      <GooglePartnerSection />
      
      <Suspense fallback={<SectionSkeleton />}>
        <TestimonialsSection />
      </Suspense>
      
      <Suspense fallback={<SectionSkeleton />}>
        <FraudClickProtectionSection />
      </Suspense>
      
      <Suspense fallback={<SectionSkeleton />}>
        <ServicesPackagesSection />
      </Suspense>
      
      <Suspense fallback={<SectionSkeleton />}>
        <FAQSection />
      </Suspense>
      
      <Suspense fallback={<SectionSkeleton />}>
        <FinalCTASection />
      </Suspense>
      
      <Suspense fallback={<SectionSkeleton />}>
        <RecentBlogPostsSection />
      </Suspense>
      
      <Suspense fallback={<SectionSkeleton />}>
        <SEOKeywordTags />
      </Suspense>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-[#012f23] to-[#059669] shadow-2xl md:hidden">
        <div className="flex items-center justify-center gap-3 p-4">
          <a
            href="https://wa.me/905326369115"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-lg font-bold text-sm glow-green"
          >
            WhatsApp
          </a>
          <a
            href="tel:05326369115"
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg font-bold text-sm glow-red"
          >
            Hemen Ara
          </a>
        </div>
      </div>
    </div>
  );
};

export default Home;
